import React, { useState, useRef, useEffect } from "react";
import useObtenerSubsecciones from "../js/translations/useObtenerSubsecciones";
import { useImmer } from "use-immer";

/**
 * Componente CheckBox
 *
 * Representa un elemento de casilla de verificación.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.itemData - Datos del elemento de casilla de verificación.
 * @param {Function} props.onChange - Función de cambio para manejar el estado de la casilla de verificación.
 * @returns {JSX.Element} Elemento de casilla de verificación.
 */
 const CheckBox = ({ itemData, onChange }) => {
  const {textosSubSecciones:traducciones} = useObtenerSubsecciones('busqueda');
  const {filtro:textosFiltros} = traducciones || {};
  const name = textosFiltros?.nombre_filtro[itemData?.displayLabel?.translationId] || itemData?.displayLabel?.default || 'N/A';

   const onKeyEnter = (event) => {
     if ((event.keyCode ? event.keyCode : event.which) == 13) {
       onChange && onChange({ ...itemData, active: (itemData.active === true || itemData.active === false) ? !itemData.active : false })
     }
   }

  return (<>
    <label className="focusable-by-children-secondary">
      <input
        className="tema-check"
        type="checkbox"
        checked={itemData?.active}
        defaultValue={itemData?.active}
        onKeyUp={(e)=>onKeyEnter(e)}
        onChange={(e) => {
          onChange && onChange({ ...itemData, active: e.target.checked })
        }}
      />
      <span className="checkmark"></span>
      {name}
    </label>
  </>)
}



/**
 * Componente CheckBoxSelector
 *
 * Representa un selector de casillas de verificación.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.itemData - Datos del selector de casillas de verificación.
 * @param {Function} props.onChange - Función de cambio para manejar el estado del selector.
 * @returns {JSX.Element} Elemento del selector de casillas de verificación.
 */

export const CheckBoxSelector = ({ itemData, onChange }) => {
  const {textosSubSecciones:traducciones} = useObtenerSubsecciones('busqueda');
  const {filtro:textosFiltros} = traducciones || {};

  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useImmer(itemData);

  const heightContainer = useRef();
  const ref = useRef();
  const sectionName = textosFiltros?.nombre_filtro[data?.displayLabel?.translationId] || data?.displayLabel?.default || 'N/A'

  const onKeyEnter = (event, item) => {
    if ((event.keyCode ? event.keyCode : event.which) == 13) {
      updateItemCheckState(item,(item.active === true || item.active === false) ? !item.active : false ) 
    }
  }

  const handleVisibility = () => {
    const container = heightContainer.current;
    if (container) {
      isOpen ? container.style.height = 'auto' : container.style.height = '0px'
    }
  }

  const updateItemCheckState = (modifiedItem, checked)=>{
    if(Array.isArray(data?.items)) {
      const items = [...data?.items].map(item=>{
        return modifiedItem?.paramValue === item?.paramValue?{...modifiedItem, active:checked}:item
      });

      const udpatedData = {
        ...data,
        items:items
      }
      setData(udpatedData)
      onChange&&onChange(udpatedData)
    } 
  }

  const getSelectedLength = ()=>{
    if(Array.isArray(data?.items)) {
      const selected = data?.items.reduce((acc,curr)=>{
        if(curr?.active){
          acc++;
        }
        return acc;
      },0)
      return selected;
    } else{
      return 0;
    }
  }

  useEffect(()=>{
    setData(itemData);
  },[itemData])

  useEffect(() => {
    handleVisibility();
  }, [isOpen])

  useEffect(() => {
    let timer = null;
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        timer = setTimeout(() => {
          setIsOpen(false);
        }, 70);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      clearTimeout(timer)
    };
  }, [ref]);

  return (
    <>
      <li className="search-group" ref={ref}>
        <button 
          role={"combobox"} 
          aria-controls={''} 
          aria-haspopup="true" 
          aria-expanded={isOpen} 
          aria-label={sectionName}  
          className="focusable-secondary dropbtn rotatebtn" onClick={() => setIsOpen(!isOpen)}>
            {sectionName}
            {(() => {
              if (isOpen) {
                return (
                  <i className="fa fa-caret-up" />
                )
              } else {
                return (
                  <i className="fa fa-caret-down" />
                )
              }
            })()}
            {(() => {
              if (getSelectedLength() > 0) {
                return (
                  <span className="numTema">{` (${getSelectedLength()})`}</span>
                )
              }
            })()}
        </button>
        <ul  role={"listbox"} aria-label={sectionName} className={`p-0 drop-content mt-1 ml-2 ${isOpen ? 'expanded' : 'collapsed'}`} ref={heightContainer}>
          {isOpen && Array.isArray(data?.items) && 
          data?.items.map((item, index) => {
            return (
              <li key={item?.paramValue} className="search-item">
                <label className="focusable-by-children-secondary">
                  <input
                    role={"menuitemcheckbox"}
                    aria-live="assertive"
                    className="mr-2 tema-check"
                    type="checkbox"
                    checked={item?.active}
                    aria-checked={item?.active}
                    onKeyUp={(e)=>onKeyEnter(e,item)}
                    onChange={(e) => {
                      updateItemCheckState(item, e.target.checked)
                    }}
                  />
                  <span className="checkmark"></span>
                  <p style={{ textAlign: "start", width: "100%" }}>{textosFiltros?.categorias[item?.displayLabel?.translationId] || item?.displayLabel?.default || 'N/A'}</p>
                </label>
              </li>
            )
          })}
        </ul>
      </li>
    </>
  )
}


export const SearchItems = {
  CheckBox,
  CheckBoxSelector
}